import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
	createUser,
	updateUserPassword,
	updateUser,
	fetchUsers
} from 'api/users/users';

import { errorNotification, extractUsername, handleApiError, isEmailValid, successNotification, warningNotification } from 'utils';

/**
 * UserModal Component
 *
 * @component
 * @param {Object} props
 * @param {number} props.usuario_id - ID del usuario (si es edición)
 * @param {string} props.action - Tipo de acción ("Nuevo" o "Editar")
 * @param {function} props.handleClose - Función para cerrar el modal
 *
 * Este componente muestra un modal para crear o editar usuarios. Contiene validación de formulario,
 * manejo de contraseñas, validación de email, generación automática de username desde el email,
 * selección de tipo de usuario y estatus, y envío de la información al backend.
 */
const UserModal = ({ usuario_id: user_id, action, handleClose }) => {
	const token = useSelector(state => state.authentication.user?.token);

	// Estado principal del formulario
	const [formUser, setFormUser] = useState({
		name: '',
		lastname: '',
		email: '',
		username: '',
		password: '',
		repeat_password: '',
		role_id: ''
	});

	const [passwordMatch, setPasswordMatch] = useState(false);
	const [selectedType, setSelectedType] = useState('');
	const [selectedStatus, setSelectedStatus] = useState('');
	
	/**
	 * Maneja la creación de un nuevo usuario (certified, candidato individual o carga masiva).
	 * Utiliza el endpoint unificado del backend para decidir automáticamente el tipo de usuario.
	 *
	 * @async
	 * @function handleCreateUser
	 * @returns {Promise<void>}
	 */
	const handleCreateUser = async () => {
		try {
			const { data } = await createUser(formUser, token);
			successNotification(data.message);
			handleCloseModal();
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Carga un usuario por ID
	 * @param {number} id
	 */
	const loadUserById = useCallback(async (id) => {
		try {
			const { data } = await fetchUsers({ id }, token);
			if (data) {
				setFormUser(data); 
				setSelectedStatus(data.status.toString());
				setSelectedType(data.role_id.toString());
			}
		} catch (error) {
			handleApiError(error);
		}
	}, [token]);

	// Cargar datos si es edición
	useEffect(() => {
		if (token && user_id > 0) {
			loadUserById(user_id);
		}
	}, [token, user_id, loadUserById]);

	/**
	 * Actualiza nombre, apellido, rol y estatus
	 */
	const handleUpdateUser = async (id) => {
		try {
			const payload = {
				name: formUser.name,
				lastname: formUser.lastname,
				role_id: selectedType,
				status: selectedStatus
			};
	
			const { data } = await updateUser(id, payload, token);
	
			if (data?.message) {
				successNotification(data.message);
				handleCloseModal();
			}
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Actualiza solo la contraseña del usuario
	 */
	const handleUpdateUserPassword = async (id) => {
		try {
			const payload = { password: formUser.password };
			await updateUserPassword(id, payload, token);
			successNotification("Contraseña actualizada con éxito.");
			handleUpdateUser(id);
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Lógica de acción general (crear o editar)
	 */
	const handleActionUser = (e) => {
		e.preventDefault();

		if (e.target.form.checkValidity()) {
			if (isEmailValid(formUser.email)) {
				if (action === "Nuevo") {
					if (passwordMatch) {
						if (formUser.role_id > 0) handleCreateUser();
						else warningNotification("Es necesario elegir un tipo de usuario.");
					}
				} else {
					if (formUser.password && formUser.repeat_password) {
						handleUpdateUserPassword(user_id);
					} else {
						handleUpdateUser(user_id);
					}
				}
			} else {
				errorNotification("El formato de email no es válido.");
			}
		} else {
			errorNotification("Faltan campos requeridos.");
		}
	};

	// Handlers de campos individuales
	const handleChangeFormUser = (e) => {
		const { name, value } = e.target;
		setFormUser(prev => ({ ...prev, [name]: value }));
	};

	const handleChangeEmail = (e) => {
		const { name, value } = e.target;
	
		// Siempre actualiza el input
		setFormUser(prev => ({
			...prev,
			[name]: value,
			username: isEmailValid(value) ? extractUsername(value) : ''
		}));	
	};

	const handleChangePassword = (e) => {
		const { name, value } = e.target;
		setFormUser(prev => ({ ...prev, [name]: value }));
		if (name === "repeat_password") {
			setPasswordMatch(value === formUser.password);
		}
	};

	const handleUserTypeChange = (e) => {
		const { value } = e.target;
		setFormUser(prev => ({ ...prev, role_id: parseInt(value) }));
		setSelectedType(value);
	};

	const handleStatusChange = (e) => setSelectedStatus(e.target.value);

	/**
	 * Cierra el modal y limpia el formulario
	 */
	const handleCloseModal = () => {
		document.getElementById('userModal').classList.remove('show');
		document.querySelector('.modal-backdrop')?.remove();
		document.getElementById('userModal').style.display = 'none';
		cleanForm();
		handleClose();
	};

	/**
	 * Limpia el formulario
	 */
	const cleanForm = () => {
		setFormUser({});
		setSelectedType('');
		setSelectedStatus('');
		setPasswordMatch(false);
	};


    return(
        <div id="userModal" className="modal modal-cover fade" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{action} Usuario</h5>
                        <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                    </div>
                    <div className="modal-body">
                        <div className="mb-3">
                            <form id="formUser" className="was-validated" >
                                <div className="row row-space-12">
                                    <div className="col-xl-12">
                                        <div className="mb-3 row" >
                                            <div className="col-6">
                                                <label className="form-label">Nombres</label>
                                                <input className="form-control" placeholder="Nombres"
                                                    id="name"
                                                    name="name"
                                                    type='text'
                                                    value={formUser.name || ''}
                                                    onChange={handleChangeFormUser}
                                                    required
                                                    pattern=".*\S+.*" />
                                                <div className="invalid-feedback" id="">
                                                    Campo requerido
                                                </div>	
                                            </div>
                                            <div className="col-6">
                                                <label className="form-label">Apellidos</label>
                                                <input className="form-control" placeholder="Apellidos"
                                                    id="lastname"
                                                    name="lastname"
                                                    required
                                                    value={formUser.lastname || ''}
                                                    onChange={handleChangeFormUser}
                                                    pattern=".*\S+.*"/>
                                                <div className="invalid-feedback" id="">
                                                    El nombre de usuario debe tener al menos 5 caracteres.
                                                </div>	
                                            </div>
                                        </div>
                                        <div className="mb-3 row" >
                                            <div className="col-6">
                                                <label className="form-label">Email</label>
                                                <input className="form-control" placeholder="Email"
                                                    id="email"
                                                    name="email"
                                                    type='email'
                                                    value={formUser.email || ''}
                                                    onChange={handleChangeEmail}
                                                    disabled={action === "Editar" ? true : false}
                                                    required />
                                                <div className="invalid-feedback" id="">
                                                    Campo requerido
                                                </div>	
                                            </div>
                                            <div className="col-6">
                                                <label className="form-label">Nombre de Usuario</label>
                                                <input className="form-control" placeholder="Nombre"
                                                    id="username"
                                                    name="username"
                                                    value={formUser.username || ''}
                                                    onChange={handleChangeFormUser}
                                                    required                                                  
                                                    pattern=".*\S+.*"
                                                    disabled />
                                                <div className="invalid-feedback" id="">
                                                    El nombre de usuario debe tener al menos 5 caracteres.
                                                </div>	
                                            </div>
                                        </div>
                                        <div className="mb-3 row" >
                                            <div className='col-xl-6'>
                                                <label className="form-label">Contraseña</label>
                                                <input className="form-control" placeholder="Contraseña"
                                                    id="password"
                                                    name="password"
                                                    type='password'
                                                    value={formUser.password || ''}
                                                    onChange={handleChangeFormUser}
                                                    required={action === "Nuevo" ? true : false}
                                                    pattern=".{8,}" />
                                                <div className="invalid-feedback" id="">
                                                    La contraseña debe tener al menos 8 caracteres.
                                                </div>	
                                            </div>
                                            <div className='col-xl-6'>
                                                <label className="form-label">Repetir Contraseña</label>
                                                <input className="form-control" placeholder="Repetir Contraseña"
                                                    id="repeat_password"
                                                    name="repeat_password"
                                                    type='password'
                                                    value={formUser.repeat_password || ''}
                                                    onChange={handleChangePassword}
                                                    required={action === "Nuevo" ? true : false}
                                                    pattern={`^${formUser.password ? formUser.password.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&") : ""}$`} />   
                                                <div className="invalid-feedback" id="">
                                                    La contraseña deben de ser igual.
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mb-3 row" >
                                            <div className='col-xl-6'>
                                                <label className="form-label">Tipo de Usuario</label>
                                                <select className="form-select" name="type" onChange={handleUserTypeChange} value={selectedType} required>
                                                    <option value="0">Seleccione un opción</option>
                                                    <option value="2">Administrador</option>
                                                    <option value="3">Examinador</option>
                                                </select>
                                            </div>
                                            {
                                                action === "Editar" && (
                                                    <div className='col-xl-6'>
                                                        <label className="form-label">Estatus</label>
                                                        <select className="form-select" name="tipos"  onChange={handleStatusChange} value={selectedStatus} >
                                                            <option value="1">Activo</option>
                                                            <option value="0">Inactivo</option>
                                                        </select>
                                                    </div>
                                                )
                                            }
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div className="modal-footer">
                    <button type="button" className="btn btn-outline-default" onClick={handleCloseModal}>Cerrar</button>
                        <button type="button" className="btn btn-outline-theme" onClick={handleActionUser} form="formUser" >{action}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UserModal;